import React from 'react';
import { InvoiceAssociationInfo } from '@/api/rebates/types';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  CircleNotchIcon,
  WarningIcon,
  InfoIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@phosphor-icons/react';
import { Card, CardContent } from '@/components/ui/card';
import { formatPaymentDate } from '@/utils/dateUtils';
import { Separator } from '@/components/ui/separator';

interface DeleteRebateInvoiceModalProps {
  open: boolean;
  invoiceInfo: InvoiceAssociationInfo | null;
  loading: boolean;
  showAdvanced: boolean;
  forceDeleteConfirmText: string;
  onSetShowAdvanced: (show: boolean) => void;
  onSetForceDeleteConfirmText: (text: string) => void;
  onConfirmDelete: (force?: boolean) => void;
  onCancel: () => void;
}

export function DeleteRebateInvoiceModal({
  open,
  invoiceInfo,
  loading,
  showAdvanced,
  forceDeleteConfirmText,
  onSetShowAdvanced,
  onSetForceDeleteConfirmText,
  onConfirmDelete,
  onCancel,
}: DeleteRebateInvoiceModalProps) {
  if (!invoiceInfo) return null;

  const isSingleAssociation = invoiceInfo.associationCount === 1;
  const isForceDeleteReady =
    forceDeleteConfirmText === invoiceInfo.invoiceNumber;
  const currentAgreementAssoc = invoiceInfo.associations[0]; // Assuming we're always viewing from one agreement's perspective

  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <AlertDialogContent
        className={`z-[1400] max-w-2xl max-h-[90vh] overflow-y-auto`}
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <WarningIcon className="h-5 w-5 text-destructive" />
            {isSingleAssociation
              ? 'Delete Vendor Reported Amount'
              : 'Remove Invoice Association'}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {isSingleAssociation
              ? 'This invoice will be permanently deleted from the system.'
              : 'Choose how to handle this invoice association.'}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-4">
          {/* Invoice Summary Card */}
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm font-medium">Invoice Number:</span>
                  <span className="text-sm font-mono">
                    {invoiceInfo.invoiceNumber}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium">Amount:</span>
                  <span className="text-sm font-semibold">
                    $
                    {invoiceInfo.totalAmount.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{' '}
                    {invoiceInfo.currency}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium">Line Items:</span>
                  <span className="text-sm">{invoiceInfo.lineItemCount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium">Documents:</span>
                  <span className="text-sm">
                    {invoiceInfo.documentCount} file(s)
                  </span>
                </div>
                {invoiceInfo.lineItemDateRange && (
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Date Range:</span>
                    <span className="text-sm">
                      {formatPaymentDate(
                        invoiceInfo.lineItemDateRange.earliest,
                      )}{' '}
                      -{' '}
                      {formatPaymentDate(invoiceInfo.lineItemDateRange.latest)}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {isSingleAssociation ? (
            /* Single Association - Cascade Delete Warning */
            <div className="space-y-3">
              <div className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                <WarningIcon className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-destructive">
                    This invoice is ONLY used by this agreement
                  </p>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <p className="font-medium">
                  The following will be PERMANENTLY DELETED:
                </p>
                <ul className="space-y-1 ml-4">
                  <li className="flex items-center gap-2">
                    <XCircleIcon className="h-4 w-4 text-destructive" />
                    Invoice record
                  </li>
                  <li className="flex items-center gap-2">
                    <XCircleIcon className="h-4 w-4 text-destructive" />
                    All {invoiceInfo.lineItemCount} line items
                  </li>
                  <li className="flex items-center gap-2">
                    <XCircleIcon className="h-4 w-4 text-destructive" />
                    {invoiceInfo.documentCount} document file(s) (original +
                    transformed)
                  </li>
                  <li className="flex items-center gap-2">
                    <XCircleIcon className="h-4 w-4 text-destructive" />
                    Forecast data for this agreement
                  </li>
                </ul>
              </div>

              <div className="flex items-start gap-2 p-3 bg-muted rounded-md">
                <InfoIcon className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground">
                  This action cannot be undone.
                </p>
              </div>
            </div>
          ) : (
            /* Multiple Associations - Remove or Force Delete Options */
            <div className="space-y-4">
              <div className="space-y-2">
                <p className="text-sm font-medium flex items-center gap-2">
                  <InfoIcon className="h-4 w-4" />
                  Currently used by {invoiceInfo.associationCount} agreement(s):
                </p>
                <div className="ml-6 space-y-1.5">
                  {invoiceInfo.associations.map((assoc) => (
                    <div
                      key={assoc.agreementId}
                      className="text-sm flex items-center gap-2"
                    >
                      <CheckCircleIcon className="h-3 w-3 text-green-600" />
                      {assoc.agreementName}
                      {assoc.agreementId ===
                        currentAgreementAssoc.agreementId && (
                        <span className="text-xs text-muted-foreground">
                          (this one)
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <p className="text-sm font-medium">What will happen:</p>
                <ul className="space-y-1 ml-4 text-sm">
                  <li className="flex items-center gap-2">
                    <CheckCircleIcon className="h-4 w-4 text-green-600" />
                    Invoice remains in database
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircleIcon className="h-4 w-4 text-green-600" />
                    Line items remain intact
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircleIcon className="h-4 w-4 text-green-600" />
                    Other {invoiceInfo.associationCount - 1} agreement(s) keep
                    using this data
                  </li>
                  <li className="flex items-center gap-2">
                    <XCircleIcon className="h-4 w-4 text-destructive" />
                    Current agreement will no longer see this invoice
                  </li>
                  <li className="flex items-center gap-2">
                    <XCircleIcon className="h-4 w-4 text-destructive" />
                    Forecast for current agreement will be invalidated
                  </li>
                </ul>
              </div>

              {/* Advanced Options - Force Delete */}
              <div className="border border-destructive/20 rounded-md">
                <button
                  type="button"
                  className="w-full p-3 flex items-center justify-between hover:bg-accent/50 transition-colors rounded-t-md"
                  onClick={() => onSetShowAdvanced(!showAdvanced)}
                >
                  <span className="text-sm font-medium flex items-center gap-2">
                    <WarningIcon className="h-4 w-4 text-destructive" />
                    Advanced: Delete from ALL agreements
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {showAdvanced ? '▲ Hide' : '▼ Show'}
                  </span>
                </button>

                {showAdvanced && (
                  <div className="p-4 space-y-3 border-t bg-destructive/5">
                    <p className="text-sm">
                      This will PERMANENTLY delete the invoice from all{' '}
                      {invoiceInfo.associationCount} agreements:
                    </p>
                    <ul className="text-sm space-y-1 ml-4">
                      {invoiceInfo.associations.map((assoc) => (
                        <li key={assoc.agreementId}>• {assoc.agreementName}</li>
                      ))}
                    </ul>

                    <div className="space-y-2 pt-2">
                      <p className="text-sm font-medium">
                        ALL of the following will be deleted:
                      </p>
                      <ul className="space-y-1 ml-4 text-sm">
                        <li className="flex items-center gap-2">
                          <XCircleIcon className="h-4 w-4 text-destructive" />
                          Invoice record
                        </li>
                        <li className="flex items-center gap-2">
                          <XCircleIcon className="h-4 w-4 text-destructive" />
                          All {invoiceInfo.lineItemCount} line items
                        </li>
                        <li className="flex items-center gap-2">
                          <XCircleIcon className="h-4 w-4 text-destructive" />
                          {invoiceInfo.documentCount} document file(s)
                        </li>
                        <li className="flex items-center gap-2">
                          <XCircleIcon className="h-4 w-4 text-destructive" />
                          Forecasts for ALL {invoiceInfo.associationCount}{' '}
                          agreements
                        </li>
                      </ul>
                    </div>

                    <div className="space-y-2 pt-2">
                      <label
                        htmlFor="confirm-text"
                        className="text-sm font-medium"
                      >
                        Type &quot;{invoiceInfo.invoiceNumber}&quot; to confirm:
                      </label>
                      <Input
                        id="confirm-text"
                        value={forceDeleteConfirmText}
                        onChange={(e) =>
                          onSetForceDeleteConfirmText(e.target.value)
                        }
                        placeholder={invoiceInfo.invoiceNumber}
                        className="font-mono"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading} onClick={onCancel}>
            Cancel
          </AlertDialogCancel>

          {isSingleAssociation ? (
            <AlertDialogAction
              onClick={() => onConfirmDelete(false)}
              disabled={loading}
              className="bg-destructive hover:bg-destructive/90"
            >
              {loading && (
                <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete Permanently
            </AlertDialogAction>
          ) : (
            <>
              <Button
                variant="destructive"
                onClick={() => onConfirmDelete(false)}
                disabled={loading}
              >
                {loading && (
                  <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
                )}
                Remove from This Agreement
              </Button>

              {showAdvanced && (
                <Button
                  variant="destructive"
                  onClick={() => onConfirmDelete(true)}
                  disabled={loading || !isForceDeleteReady}
                  className="bg-red-700 hover:bg-red-800"
                >
                  {loading && (
                    <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Delete from ALL Agreements
                </Button>
              )}
            </>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
